import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertTriangle,
  TrendingDown,
  DollarSign,
  UserX,
  Activity,
  Check,
  CheckCheck,
  X,
  MapPin,
  Unplug,
  TrendingUp,
  Gauge,
  Lightbulb,
  Trophy,
  TriangleAlert,
  Video,
  Package,
  ShoppingCart,
  Handshake,
  Phone,
  Store,
  RefreshCcw,
} from "lucide-react";
import { ListSkeleton } from "@/components/skeletons";

interface MonitoringAlert {
  id: string;
  alertType: string;
  severity: string;
  accountId: string | null;
  accountName: string | null;
  metricName: string | null;
  actualValue: string | null;
  targetValue: string | null;
  message: string;
  recommendation: string | null;
  isRead: boolean;
  createdAt: string;
}

interface MonitoringAlertsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ALERT_ICON_MAP: Record<string, typeof AlertTriangle> = {
  cpl_exceeded: DollarSign,
  cpc_exceeded: DollarSign,
  roas_below: TrendingDown,
  no_leads: UserX,
  spend_anomaly: Activity,
  placement_waste: MapPin,
  zero_conversions: Unplug,
  budget_increase: TrendingUp,
  high_cpm: Gauge,
  early_winner_signal: Trophy,
  early_loser_signal: TriangleAlert,
  webinar_low_show_rate: Video,
  webinar_low_offer_rate: Video,
  shopify_low_stock: Package,
  shopify_out_of_stock: Package,
  shopify_revenue_drop: ShoppingCart,
  crm_low_close_rate: Handshake,
  crm_low_show_rate: UserX,
  crm_no_new_leads: UserX,
  baselinker_low_stock: Package,
  baselinker_revenue_drop: ShoppingCart,
  ringostat_low_answer_rate: Phone,
  ringostat_low_call_volume: Phone,
  allegro_sales_drop: Store,
  allegro_low_roas: Store,
  hubspot_low_close_rate: Handshake,
  hubspot_pipeline_stall: RefreshCcw,
};

const SEVERITY_STYLES: Record<string, { bg: string; border: string; badge: string }> = {
  critical: {
    bg: "bg-red-500/10",
    border: "border-red-500/30",
    badge: "bg-red-500/20 text-red-400 border-red-500/30",
  },
  warning: {
    bg: "bg-yellow-500/10",
    border: "border-yellow-500/30",
    badge: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  },
  info: {
    bg: "bg-blue-500/10",
    border: "border-blue-500/30",
    badge: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  },
};

const SEVERITY_LABEL_KEYS: Record<string, string> = {
  critical: "monitoringAlerts.severity.critical",
  warning: "monitoringAlerts.severity.warning",
  info: "monitoringAlerts.severity.info",
};

function useFormatTimeAgo() {
  const { t } = useTranslation();
  return (dateStr: string): string => {
    const now = Date.now();
    const then = new Date(dateStr).getTime();
    const diffMs = now - then;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return t("monitoringAlerts.time.justNow");
    if (diffMin < 60) return t("monitoringAlerts.time.minutesAgo", { count: diffMin });
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return t("monitoringAlerts.time.hoursAgo", { count: diffHours });
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return t("monitoringAlerts.time.yesterday");
    return t("monitoringAlerts.time.daysAgo", { count: diffDays });
  };
}

const METRIC_SUFFIX: Record<string, string> = {
  cost_per_lead: " zł",
  cost_per_call: " zł",
  cost_per_purchase: " zł",
  cost_per_signup: " zł",
  cpm: " zł",
  roas: "x",
  placement_spend_share: "%",
};

function formatMetricValue(raw: string, metricName: string | null): string {
  const val = parseFloat(raw);
  if (isNaN(val)) return raw;
  const suffix = METRIC_SUFFIX[metricName ?? ""] ?? "";
  return `${val.toFixed(2)}${suffix}`;
}

export function MonitoringAlertsPanel({ open, onOpenChange }: MonitoringAlertsPanelProps) {
  const { t } = useTranslation();
  const formatTimeAgo = useFormatTimeAgo();
  const queryClient = useQueryClient();

  const { data: alerts = [], isLoading } = useQuery<MonitoringAlert[]>({
    queryKey: ["/api/monitoring/alerts"],
    queryFn: () => apiRequest("GET", "/api/monitoring/alerts"),
    enabled: open,
    refetchInterval: open ? 4 * 60 * 60 * 1000 : false,
  });

  const markReadMutation = useMutation({
    mutationFn: (alertId: string) => apiRequest("PATCH", `/api/monitoring/alerts/${alertId}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/monitoring/alerts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/monitoring/alerts/count"] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => apiRequest("PATCH", "/api/monitoring/alerts/read-all"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/monitoring/alerts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/monitoring/alerts/count"] });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: (alertId: string) => apiRequest("DELETE", `/api/monitoring/alerts/${alertId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/monitoring/alerts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/monitoring/alerts/count"] });
    },
  });

  const unreadCount = alerts.filter((a) => !a.isRead).length;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[420px] sm:w-[480px] bg-[#0d1117] border-l border-white/10 p-0"
      >
        <SheetHeader className="p-4 pb-3 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <SheetTitle className="text-white text-sm font-semibold tracking-wide uppercase">
                  {t("monitoringAlerts.title")}
                </SheetTitle>
              </div>
              {unreadCount > 0 && (
                <Badge
                  variant="outline"
                  className="bg-red-500/20 text-red-400 border-red-500/30 text-xs px-2"
                >
                  {t("monitoringAlerts.alertsBadge", { count: unreadCount })}
                </Badge>
              )}
            </div>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => markAllReadMutation.mutate()}
                className="text-white/50 hover:text-white text-xs h-7 px-2"
              >
                <CheckCheck className="w-3.5 h-3.5 mr-1" />
                {t("monitoringAlerts.markAll")}
              </Button>
            )}
          </div>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-80px)]">
          <div className="p-3 space-y-2">
            {/* VP-208: placeholder listy alertów zamiast tekstu „ładowanie…". */}
            {isLoading && <ListSkeleton rows={5} withAvatar={false} />}

            {!isLoading && alerts.length === 0 && (
              <div className="text-center py-12">
                <Activity className="w-10 h-10 text-emerald-500/40 mx-auto mb-3" />
                <p className="text-white/50 text-sm font-medium">
                  {t("monitoringAlerts.empty.title")}
                </p>
                <p className="text-white/30 text-xs mt-1">{t("monitoringAlerts.empty.desc")}</p>
              </div>
            )}

            {alerts.map((alert) => {
              const Icon = ALERT_ICON_MAP[alert.alertType] ?? AlertTriangle;
              const styles = SEVERITY_STYLES[alert.severity] ?? SEVERITY_STYLES.info;

              return (
                <div
                  key={alert.id}
                  className={`
                    rounded-lg border p-3 transition-all
                    ${styles.bg} ${styles.border}
                    ${alert.isRead ? "opacity-60" : ""}
                  `}
                >
                  {/* Top: icon + message */}
                  <div className="flex gap-3">
                    <div
                      className={`
                      w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0
                      ${alert.severity === "critical" ? "bg-red-500/20 text-red-400" : ""}
                      ${alert.severity === "warning" ? "bg-yellow-500/20 text-yellow-400" : ""}
                      ${alert.severity === "info" ? "bg-blue-500/20 text-blue-400" : ""}
                    `}
                    >
                      <Icon className="w-4 h-4" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-white/90 text-sm font-medium leading-snug">
                        {alert.message}
                      </p>

                      <div className="flex items-center gap-2 mt-1.5">
                        <Badge
                          variant="outline"
                          className={`${styles.badge} text-[10px] px-1.5 py-0`}
                        >
                          {SEVERITY_LABEL_KEYS[alert.severity]
                            ? t(SEVERITY_LABEL_KEYS[alert.severity])
                            : alert.severity}
                        </Badge>
                        {alert.accountName && (
                          <span className="text-white/30 text-[10px]">{alert.accountName}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Recommendation */}
                  {alert.recommendation && (
                    <div className="flex gap-2 mt-2 mx-1 p-2 rounded-md bg-white/5 border border-white/5">
                      <Lightbulb className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <p className="text-white/60 text-xs leading-relaxed">
                        {alert.recommendation}
                      </p>
                    </div>
                  )}

                  {/* Bottom: timestamp + actions */}
                  <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-white/5">
                    <span className="text-white/25 text-[10px]">
                      {formatTimeAgo(alert.createdAt)}
                    </span>
                    <div className="flex gap-1.5">
                      {!alert.isRead && (
                        <button
                          onClick={() => markReadMutation.mutate(alert.id)}
                          className="flex items-center gap-1 text-white/25 hover:text-emerald-400 px-1.5 py-0.5 rounded hover:bg-white/5 transition-colors text-[10px]"
                        >
                          <Check className="w-3 h-3" />
                          {t("monitoringAlerts.markRead")}
                        </button>
                      )}
                      <button
                        onClick={() => dismissMutation.mutate(alert.id)}
                        className="flex items-center gap-1 text-white/25 hover:text-red-400 px-1.5 py-0.5 rounded hover:bg-white/5 transition-colors text-[10px]"
                      >
                        <X className="w-3 h-3" />
                        {t("monitoringAlerts.dismiss")}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

/** Hook for the sidebar badge count */
export function useMonitoringAlertCount() {
  const { data } = useQuery<{ count: number }>({
    queryKey: ["/api/monitoring/alerts/count"],
    queryFn: () => apiRequest("GET", "/api/monitoring/alerts/count"),
    refetchInterval: 4 * 60 * 60 * 1000,
    staleTime: 4 * 60 * 60 * 1000,
  });
  return data?.count ?? 0;
}
